import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Heart, ChatCircle, User, GearSix, Crown, Sun, Moon, List, ShieldCheck, Globe } from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import NotificationBell from './NotificationBell';
import { useLang } from '../contexts/LanguageContext';
import { LANGUAGE_OPTIONS } from '../lib/translations';

export default function Navbar({ darkMode, setDarkMode }) {
  const { user, logout } = useAuth();
  const { lang, switchLang, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/', { replace: true });
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const langMenuRef = useRef(null);

  // Close mobile menu & lang dropdown on outside click / Escape / route change
  useEffect(() => {
    if (!menuOpen && !langOpen) return;
    const handleClick = (e) => {
      if (
        menuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target) &&
        mobileToggleRef.current &&
        !mobileToggleRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
      if (langOpen && langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') { setMenuOpen(false); setLangOpen(false); } };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen, langOpen]);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const navLinks = user ? [
    { to: '/browse', icon: Heart, label: 'Browse' },
    { to: '/chat', icon: ChatCircle, label: 'Chat' },
    { to: '/profile/me', icon: User, label: 'Profile' },
    { to: '/settings', icon: GearSix, label: 'Settings' },
  ] : [];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50 px-4 py-3" data-testid="navbar">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <Link to={user ? '/browse' : '/'} className="flex items-center gap-2" data-testid="nav-logo">
          <Heart weight="fill" size={24} className="text-[#D4AF37]" />
          <span className="text-xl font-bold font-['Outfit'] text-white">
            Yu<span className="text-gold-gradient">Blink</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                isActive(to)
                  ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                  : 'text-[#9EABCE] hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon weight={isActive(to) ? 'fill' : 'light'} size={20} />
              {label}
            </Link>
          ))}

          {user && !user.is_premium && (
            <Link
              to="/premium"
              data-testid="nav-premium"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 hover:bg-[#D4AF37]/20 transition-all"
            >
              <Crown weight="fill" size={18} />
              Premium
            </Link>
          )}

          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-all"
            >
              <ShieldCheck weight="fill" size={18} />
              Admin
            </Link>
          )}

          <NotificationBell />

          {/* Language Selector */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="lang-selector"
              className="p-2 rounded-xl text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all flex items-center gap-1"
            >
              <Globe weight="light" size={20} />
              <span className="text-xs font-medium uppercase">{lang}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-1 rounded-xl overflow-hidden z-50 min-w-[140px] bg-[#0B112B] border border-[#D4AF37]/20 shadow-[0_8px_32px_rgba(0,0,0,0.6)]" data-testid="lang-dropdown">
                {LANGUAGE_OPTIONS.map(opt => (
                  <button
                    key={opt.code}
                    onClick={() => { switchLang(opt.code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-all ${
                      lang === opt.code ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#9EABCE] hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span>{opt.flag}</span> {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setDarkMode(!darkMode)}
            data-testid="theme-toggle"
            className="p-2 rounded-xl text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all"
          >
            {darkMode ? <Sun weight="light" size={20} /> : <Moon weight="light" size={20} />}
          </button>

          {user && (
            <button
              onClick={handleLogout}
              data-testid="nav-logout"
              className="px-4 py-2 rounded-xl text-sm text-[#9EABCE] hover:text-red-400 hover:bg-red-400/5 transition-all"
            >
              Logout
            </button>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center gap-1">
          <NotificationBell />
          <button
            className="p-2 text-[#9EABCE]"
            onClick={() => setMenuOpen(!menuOpen)}
            ref={mobileToggleRef}
            data-testid="mobile-menu-toggle"
          >
            <List size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div ref={mobileMenuRef} className="md:hidden glass-card mt-2 mx-2 p-4 space-y-2" data-testid="mobile-menu">
          {navLinks.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isActive(to) ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#9EABCE] hover:text-white'
              }`}
            >
              <Icon weight={isActive(to) ? 'fill' : 'light'} size={20} />
              {label}
            </Link>
          ))}
          {user && !user.is_premium && (
            <Link to="/premium" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-[#D4AF37]">
              <Crown weight="fill" size={18} /> Premium
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-nav-admin"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold bg-red-500/10 text-red-400 border border-red-500/20"
            >
              <ShieldCheck weight="fill" size={18} /> Admin
            </Link>
          )}
          {/* Language selector */}
          <div className="px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9EABCE]/60 mb-2">Language</div>
            <div className="flex flex-wrap gap-1.5">
              {LANGUAGE_OPTIONS.map(opt => (
                <button
                  key={opt.code}
                  onClick={() => { switchLang(opt.code); }}
                  data-testid={`mobile-lang-${opt.code}`}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
                    lang === opt.code ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30' : 'bg-white/5 text-[#9EABCE]'
                  }`}
                >
                  <span>{opt.flag}</span> {opt.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <button onClick={() => setDarkMode(!darkMode)} className="text-[#9EABCE] hover:text-white">
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            {user && (
              <button onClick={handleLogout} className="text-sm text-red-400">
                Logout
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
